import React, { useState } from "react";
import { Modal, Tag, Button, Divider, Popconfirm, Drawer, Empty } from "antd";
import {
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  HomeOutlined,
  CreditCardOutlined,
  TeamOutlined,
  WifiOutlined,
  CarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { BOOKING_INVOICE, IMAGE_BASE_URL } from "../../constants/api";
import LS_SERVICE from "../../utils/localStorage";
import { getRoomTypeLabel, getServiceLabel } from "../../constants/constent";

/* ─── Helpers ──────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  0: { label: "Pending", color: "#EF9F27", bg: "rgba(239,159,39,0.12)" },
  1: { label: "Ongoing", color: "#185FA5", bg: "rgba(24,95,165,0.12)" },
  2: { label: "Completed", color: "#1D9E75", bg: "rgba(29,158,117,0.12)" },
  3: { label: "Cancelled", color: "#D85A30", bg: "rgba(216,90,48,0.12)" },
  4: { label: "Accepted", color: "#639922", bg: "rgba(99,153,34,0.12)" },
};

/* ─── Styles (scoped via prefix) ──────────────────────────────── */
const styles = {
  modal: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  hero: {
    position: "relative",
    height: 200,
    overflow: "hidden",
    borderRadius: "12px 12px 0 0",
    marginBottom: 0,
  },
  heroImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.60))",
  },
  statusPill: (status) => ({
    position: "absolute",
    top: 14,
    right: 14,
    padding: "4px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    color: "#fff",
    background: STATUS_CONFIG[status]?.color || "#888",
    letterSpacing: "0.3px",
    zIndex: 1,
  }),
  hotelBadge: {
    position: "absolute",
    bottom: 14,
    left: 16,
    color: "#fff",
    zIndex: 1,
  },
  hotelName: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    fontFamily: "'Playfair Display', Georgia, serif",
    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
  },
  hotelLocation: {
    margin: 0,
    fontSize: 12,
    opacity: 0.85,
  },
  thumbnailRow: {
    display: "flex",
    gap: 6,
    padding: "12px 20px 0",
  },
  thumb: (active) => ({
    width: 58,
    height: 44,
    borderRadius: 6,
    objectFit: "cover",
    cursor: "pointer",
    border: `2px solid ${active ? "#1D9E75" : "transparent"}`,
    transition: "border-color 0.15s",
  }),
  body: {
    padding: "16px 22px 22px",
  },
  guestRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "rgba(24,95,165,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 500,
    fontSize: 15,
    color: "#185FA5",
    flexShrink: 0,
  },
  guestName: { margin: 0, fontSize: 15, fontWeight: 500 },
  guestEmail: { margin: 0, fontSize: 12, color: "#888" },
  bookingIdBox: { marginLeft: "auto", textAlign: "right" },
  bookingIdLabel: { margin: 0, fontSize: 11, color: "#888" },
  bookingIdVal: { margin: 0, fontSize: 13, fontWeight: 500 },
  datesRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 10,
    alignItems: "center",
    background: "#f8f8f6",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 16,
  },
  dateBlock: (right) => ({
    textAlign: right ? "right" : "left",
  }),
  dateLabel: {
    margin: 0,
    fontSize: 11,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  dateValue: { margin: "3px 0 2px", fontSize: 16, fontWeight: 500 },
  dateTime: { margin: 0, fontSize: 11, color: "#888" },
  nightsBadge: { textAlign: "center" },
  nightsPill: {
    background: "#fff",
    border: "0.5px solid rgba(0,0,0,0.12)",
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  nightsLabel: { margin: "4px 0 0", fontSize: 10, color: "#aaa" },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 16,
  },
  detailItem: {
    padding: "12px 14px",
    border: "0.5px solid rgba(0,0,0,0.1)",
    borderRadius: 8,
  },
  detailLabel: {
    margin: 0,
    fontSize: 11,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  detailValue: { margin: "3px 0 0", fontSize: 13, fontWeight: 500 },
  amenitiesLabel: {
    margin: "0 0 8px",
    fontSize: 11,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 },
  chip: {
    padding: "5px 12px",
    background: "#f5f5f2",
    border: "0.5px solid rgba(0,0,0,0.08)",
    borderRadius: 20,
    fontSize: 12,
    color: "#555",
  },
  sectionTitle: {
    margin: "0 0 12px",
    fontSize: 11,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  amountRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  amountLabel: { fontSize: 13, color: "#666" },
  amountValue: { fontSize: 13, fontWeight: 500 },
  totalBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#f8f8f6",
    borderRadius: 10,
    padding: "12px 16px",
    margin: "10px 0 6px",
  },
  totalLabel: { fontSize: 13, fontWeight: 500 },
  totalValue: { fontSize: 20, fontWeight: 500 },
  balanceRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  balanceLabel: { fontSize: 12, color: "#888" },
  balanceValue: { fontSize: 13, fontWeight: 500, color: "#D85A30" },
  actionsRow: { display: "flex", gap: 10 },
};

/* ─── Component ────────────────────────────────────────────────── */
const CustomerBookingModal = ({
  visible,
  onClose,
  booking,
  handleCheckout,
  payInit,
  loading
}) => {
  const [activeThumb, setActiveThumb] = useState(0);
  const { company_name, company_address } = LS_SERVICE.get("setting") || {};

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState("");

  const openInvoice = (url) => {
    setInvoiceUrl(url);
    setDrawerOpen(true);
  };

  if (!booking) return null;

  const {
    id,
    customer_name,
    room_category_title,
    user,
    room,
    order_id,
    multi_book_id,
    room_type,
    total_days,
    checkin_date,
    checkout_date,
    booking_segment,
    payment_method,
    payment_status,
    total_with_gst,
    bill_amount,
    booking_status,
    advance_amount,
    order_amount,
    ex_service_amount,
    rate_plans,
    room_images = room.room_images.map(
      (e) => IMAGE_BASE_URL + e.image.filePath,
    ),
    room_facility = room.room_facility.map((e) => e.facility.title),
  } = booking;

  const formattedCheckIn = moment(checkin_date).format("D MMM YY");
  const formattedCheckOut = moment(checkout_date).format("D MMM YY");
  const checkInDay = moment(checkin_date).format("ddd, hh:mm A");
  const checkOutDay = moment(checkout_date).format("ddd, hh:mm A");

  // Initials for avatar
  const initials = customer_name
    ? customer_name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  const totalAmount =
    parseFloat(total_with_gst || 0) +
    parseFloat(order_amount || 0) +
    parseFloat(ex_service_amount || 0);

  const balance = parseFloat(bill_amount || 0).toFixed(2);

  // Room images — use provided images or fall back to placeholder
  const images =
    room_images.length > 0
      ? room_images
      : [
          "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=700&q=80",
          "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=700&q=80",
          "https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=700&q=80",
          "https://images.unsplash.com/photo-1571508601891-ca5e7a713859?w=700&q=80",
        ];

  const thumbImages = images.map((img) =>
    img.includes("unsplash") ? img.replace("w=700", "w=120") : img,
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={660}
      centered
      styles={{ body: { padding: 0 } }}
      style={styles.modal}
      closable
    >
      {/* Hero Image */}
      <div style={styles.hero}>
        <img src={images[activeThumb]} alt="Room" style={styles.heroImg} />
        <div style={styles.heroOverlay} />
        <span style={styles.statusPill(booking_status)}>
          {STATUS_CONFIG[booking_status]?.label || "Unknown"}
        </span>
        <div style={styles.hotelBadge}>
          <p style={styles.hotelName}>{company_name}</p>
          <p style={styles.hotelLocation}>{company_address}</p>
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div style={styles.thumbnailRow}>
          {images.map((img, i) => (
            <img
              key={i}
              src={thumbImages[i]}
              alt={`Room view ${i + 1}`}
              style={styles.thumb(activeThumb === i)}
              onClick={() => setActiveThumb(i)}
            />
          ))}
        </div>
      )}

      <div style={styles.body}>
        {/* Guest Info */}
        <div style={styles.guestRow}>
          <div style={styles.avatar}>{initials}</div>
          <div>
            <p style={styles.guestName}>{customer_name}</p>
            <p style={styles.guestEmail}>{user?.email || "—"}</p>
          </div>
          <div style={styles.bookingIdBox}>
            <p style={styles.bookingIdLabel}>Booking ID</p>
            <p style={styles.bookingIdVal}>{order_id || `#${id}`}</p>
          </div>
        </div>

        {/* Dates */}
        <div style={styles.datesRow}>
          <div style={styles.dateBlock(false)}>
            <p style={styles.dateLabel}>Check-in</p>
            <p style={styles.dateValue}>{formattedCheckIn}</p>
            <p style={styles.dateTime}>{checkInDay}</p>
          </div>
          <div style={styles.nightsBadge}>
            <div style={styles.nightsPill}>
              {total_days} {total_days === 1 ? "Night" : "Nights"}
            </div>
            <p style={styles.nightsLabel}>duration</p>
          </div>
          <div style={styles.dateBlock(true)}>
            <p style={styles.dateLabel}>Check-out</p>
            <p style={styles.dateValue}>{formattedCheckOut}</p>
            <p style={styles.dateTime}>{checkOutDay}</p>
          </div>
        </div>

        {/* Details Grid */}
        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <p style={styles.detailLabel}>Room Type</p>
            <p style={styles.detailValue}>{room_category_title || "—"}</p>
          </div>
          <div style={styles.detailItem}>
            <p style={styles.detailLabel}>Guests</p>
            <p style={styles.detailValue}>
              {getRoomTypeLabel(room_type) || "—"}
            </p>
          </div>
          <div style={styles.detailItem}>
            <p style={styles.detailLabel}>Meal Plan</p>
            <p style={styles.detailValue}>
              {getServiceLabel(rate_plans?.room_service) || "—"}
            </p>
          </div>
          <div style={styles.detailItem}>
            <p style={styles.detailLabel}>Payment Mode</p>
            <p style={styles.detailValue}>{payment_method || "—"}</p>
          </div>
        </div>

        {/* Amenities */}
        <p style={styles.amenitiesLabel}>Room Amenities</p>
        <div style={styles.chipRow}>
          {room_facility.map((a) => (
            <span key={a} style={styles.chip}>
              {a}
            </span>
          ))}
        </div>

        <Divider style={{ margin: "0 0 16px" }} />

        {/* Payment Summary */}
        <p style={styles.sectionTitle}>Payment Summary</p>

        <div style={styles.amountRow}>
          <span style={styles.amountLabel}>
            Room charges ({total_days} nights)
          </span>
          <span style={styles.amountValue}>
            ₹{parseFloat(total_with_gst || 0).toFixed(2)}
          </span>
        </div>
        {parseFloat(ex_service_amount) > 0 && (
          <div style={styles.amountRow}>
            <span style={styles.amountLabel}>Extra services</span>
            <span style={styles.amountValue}>
              ₹{parseFloat(ex_service_amount).toFixed(2)}
            </span>
          </div>
        )}
        {parseFloat(order_amount) > 0 && (
          <div style={styles.amountRow}>
            <span style={styles.amountLabel}>Food orders</span>
            <span style={styles.amountValue}>
              ₹{parseFloat(order_amount).toFixed(2)}
            </span>
          </div>
        )}
        {parseFloat(advance_amount) > 0 && (
          <div style={styles.amountRow}>
            <span style={styles.amountLabel}>Advance paid</span>
            <span style={{ ...styles.amountValue, color: "#1D9E75" }}>
              − ₹{parseFloat(advance_amount).toFixed(2)}
            </span>
          </div>
        )}

        <div style={styles.totalBox}>
          <span style={styles.totalLabel}>Total Amount</span>
          <span style={styles.totalValue}>₹{totalAmount.toFixed(2)}</span>
        </div>
        <div style={styles.balanceRow}>
          <span style={styles.balanceLabel}>Balance due at property</span>
          <span style={styles.balanceValue}>₹{balance}</span>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionsRow}>
          {booking_status === "2" && payment_status === "1" && (
            <Button
              loading={loading}
              disabled={loading}
              block
              style={{ flex: 1 }}
              onClick={() => {
                const encodedId = btoa(id.toString());
                openInvoice(`${BOOKING_INVOICE}${encodedId}`);
              }}
            >
              View Invoice
            </Button>
          )}
          {payment_method === "razorpay" && payment_status === "0" && (
            <Button
              loading={loading}
              disabled={loading}
              onClick={() => payInit(id)}
              block
              type="primary"
              style={{ flex: 1, background: "#b61818", border: "none" }}
            >
              Pay, Due Bill
            </Button>
          )}

          {booking_status === "1" && (
            <Popconfirm
              title="Checkout the booking"
              description="Are you sure to checkout?"
              onConfirm={handleCheckout}
              okText="Yes"
              cancelText="No"
            >
              <Button loading={loading} disabled={loading} block>
                Checkout
              </Button>
            </Popconfirm>
          )}

          <Button
            loading={loading}
            disabled={loading}
            onClick={onClose}
            block
            danger
            style={{ flex: 1 }}
          >
            Close
          </Button>
        </div>
      </div>

      {/* DRAWER */}
      <Drawer
        title="Invoice Preview"
        placement="right"
        width="70%"
        onClose={() => {
          setInvoiceUrl("");
          setDrawerOpen(false);
        }}
        open={drawerOpen}
      >
        {invoiceUrl ? (
          <iframe
            src={invoiceUrl}
            title="Invoice"
            style={{
              width: "100%",
              height: "85vh",
              border: "none",
              borderRadius: 8,
            }}
          />
        ) : (
          <Empty description="No Invoice Found" />
        )}
      </Drawer>
    </Modal>
  );
};

export default CustomerBookingModal;
